/**
 * Collection landing pages — the keyword-addressable layer over `product.category`.
 *
 * Why this file exists: every commercial query in this niche is answered by a *category*
 * page, not a homepage and not a product page. Competitor pages that rank for
 * "artificial flowers for home decoration" are collection URLs (masonhome.in
 * /collections/artificial-flowers and equivalents), each with the keyword in the URL, the
 * title and the H1. Before this file the only way to browse a category here was
 * `/shop?category=Flowering%20Plants` — a query-string URL, which the prerender filter in
 * vite.config.ts excludes and which search engines treat as a facet of /shop rather than a
 * page of its own. So the site had four categories and zero indexable category pages.
 *
 * The `slug` is deliberately NOT derived from `category`. "Flowering Plants" and
 * "Decorative Topiary" are how the catalogue is organised internally; "artificial-flowers"
 * and "artificial-topiary" are what people type. Deriving one from the other would encode
 * the boutique vocabulary into the URLs, which is the exact problem being fixed.
 *
 * Single source of truth on purpose: the route (src/routes/collections/$slug.tsx), the
 * prerender page list (vite.config.ts) and the sitemap generator
 * (scripts/generate-sitemap.mjs) all read from here. A collection that exists in one place
 * but not the others is either an orphan page or a 404 in the sitemap, and both are
 * invisible until a crawler finds them.
 *
 * Every claim in the copy below is checked against src/lib/products.ts. All 19 products are
 * 18–29 cm tall and every one lists a pot or planter in `potDetails`, which is why "pot
 * included" and the tabletop framing appear throughout — those are the two things this
 * catalogue can honestly say that the big floor-plant sellers cannot. Nothing here states a
 * material, a warranty, a delivery time or a price, because the catalogue does not record
 * them and inventing them would be a business claim rather than a description.
 */

export type CollectionDef = {
  /** URL segment: /collections/<slug>. Keyword-led, not derived from `category`. */
  slug: string;
  /** Must match `product.category` in src/lib/products.ts exactly (case-insensitive compare). */
  category: string;
  /** <h1>. The search term, in plain words. */
  heading: string;
  /** Sits under the h1 — where the brand voice lives, per the dual-vocabulary approach. */
  subheading: string;
  /** <title> prefix. Kept under ~60 chars including the brand suffix. */
  title: string;
  /** Meta description. Written to be read by a human, with the synonyms people search. */
  description: string;
  /** Opening body paragraph. Real search vocabulary in prose rather than a keyword list. */
  intro: string;
  /** Two Q&As per collection, emitted as FAQPage schema and rendered visibly. */
  faqs: { question: string; answer: string }[];
};

export const collections: CollectionDef[] = [
  {
    slug: "artificial-bonsai",
    category: "Artificial Bonsai",
    heading: "Artificial Bonsai Trees",
    subheading: "Sculpted trunks and worked canopies, without the decade of pruning.",
    title: "Artificial Bonsai Trees with Pot",
    description:
      "Faux bonsai trees for desks and shelves — artificial cherry blossom, cedar and Japanese pine bonsai, each with a ceramic pot included.",
    intro:
      "A real bonsai is a commitment measured in years: watering schedules, root pruning, a hard look at where the light falls. An artificial bonsai tree gives the same worked silhouette — a sculpted trunk, a canopy shaped rather than merely grown — and asks for a dusting instead. These are desk-scale pieces, 22 to 28 cm tall, and each one arrives seated in its own pot.",
    faqs: [
      {
        question: "What size are these artificial bonsai trees?",
        answer:
          "Between 22 cm and 28 cm tall, and 20 to 28 cm across the canopy. Exact height and width are listed on each product page.",
      },
      {
        question: "Is a pot included with the bonsai?",
        answer:
          "Yes — every bonsai in this collection ships potted in a dedicated ceramic planter, fluted dish or footed bonsai pot.",
      },
    ],
  },
  {
    slug: "trees-and-plants",
    category: "Trees & Plants",
    heading: "Artificial Trees & Plants",
    subheading: "5 FT statement greenery that commands attention in living spaces and offices.",
    title: "Artificial Trees & Tall Plants",
    description:
      "5 FT tall artificial statement trees — maple, fiddle leaf fig, olive, bamboo and palm trees with stylish pots included.",
    intro:
      "Transform empty corners and high-ceiling spaces with our 5 FT statement trees. From Japanese autumn maples and Mediterranean olive trees to lush fiddle-leaf figs, natural cane bamboo and tropical palms, each tree is crafted with textured trunks and realistic foliage in designer ceramic pots.",
    faqs: [
      {
        question: "How tall are these artificial trees?",
        answer:
          "All trees in this collection stand at 5 FT (152 cm) in height, designed to make an instant grand statement in entryways, living rooms, and offices.",
      },
      {
        question: "Do the trees come with pots?",
        answer:
          "Yes, every 5 FT tree arrives seated in a stylish weighted planter or fluted ceramic pot, ready to display out of the box.",
      },
    ],
  },
  {
    slug: "flower-bushes",
    category: "Flower Bushes",
    heading: "Artificial Flower Bushes",
    subheading: "Vibrant blooming bougainvillea, trumpet lilies, sunflowers & rose shrubs.",
    title: "Artificial Flower Bushes with Pot",
    description:
      "Buy artificial flower bushes for home decoration online — fake flower bushes, silk lilies, sunflowers and bougainvillea shrubs, each with a pot included.",
    intro:
      "Artificial flower bushes earn their place where real ones struggle: a side table, an office desk, a bathroom windowsill, a mantelpiece nobody remembers to water. These are lush blooming bushes rather than cut stems, arranged and potted with vibrant long-lasting petals.",
    faqs: [
      {
        question: "Do these artificial flower bushes come with a pot?",
        answer:
          "Yes. Every flowering bush in this collection includes its own matching planter — white tapered, fluted bowl, matte round, or square ceramic.",
      },
      {
        question: "How do I clean artificial flower bushes?",
        answer:
          "Wipe the foliage gently with a soft dry cloth. No water, no spray and no polish — dusting is all the upkeep required.",
      },
    ],
  },
  {
    slug: "hanging-plants",
    category: "Hanging",
    heading: "Artificial Hanging Plants & Vines",
    subheading: "Cascading floral vines and trailing greenery to bring life to your walls.",
    title: "Artificial Hanging Plants & Floral Vines",
    description:
      "Cascading artificial floral vines and trailing wall creepers — peach rose, daisy, flame flower and carnation vines.",
    intro:
      "Bring life to your walls, balconies, and pergolas with trailing floral vines. Each piece cascades between 80 cm and 90 cm with dense lifelike greenery and vibrant blossom clusters that stay fresh year after year.",
    faqs: [
      {
        question: "How long are these hanging vines?",
        answer:
          "Each trailing vine has a cascade length of 80 to 90 cm, providing dramatic vertical drape for walls, bookshelves, and railings.",
      },
      {
        question: "Are hanging fixtures included?",
        answer:
          "Yes, every hanging vine includes wall mounting hooks or hanging anchors for effortless installation.",
      },
    ],
  },
  {
    slug: "flower-plants-and-pots",
    category: "Flower Plants and Pots",
    heading: "Flower Plants & Designer Pots",
    subheading: "Lush tropical potted foliage and lotus blossoms in ceramic and fluted planters.",
    title: "Potted Flower Plants with Designer Pots",
    description:
      "Potted artificial botanicals — lotus blossoms, bird of paradise, monstera, calathea and caladium with designer pots included.",
    intro:
      "Discover tabletop and floor botanicals paired with modern ceramic and textured pots. Spanning split-leaf monsteras, red-veined caladiums, purple calatheas, bird of paradise heliconias, and lotus blossom stems.",
    faqs: [
      {
        question: "What types of pots are included?",
        answer:
          "Our collection features white fluted ceramics, embossed stone planters, modern slate grey pots, and octagonal designer vessels.",
      },
      {
        question: "Are these plants suitable for low light rooms?",
        answer:
          "Yes, all artificial potted plants thrive anywhere indoors, independent of sunlight, temperature, or watering.",
      },
    ],
  },
  {
    slug: "flower-bunches",
    category: "Flower Bunches",
    heading: "Artificial Flower Bunches",
    subheading: "Vibrant hand-assembled cut stems, ready to arrange in your own vase.",
    title: "Artificial Flower Bunches for Vases",
    description:
      "Hand-assembled artificial flower bunches — vivid cut stems of silk and polyester petals, ready to drop into a vase, bottle or planter.",
    intro:
      "A flower bunch is the one arrangement that belongs on a table, a mantelpiece or a bedside shelf the day it arrives — no watering, no wilting. These are dense, hand-assembled cut-stem bunches of long-lasting petals, shipped ready to display in any vase or bottle you already own.",
    faqs: [
      {
        question: "Do these flower bunches come with a vase?",
        answer:
          "No — each bunch is cut stems only, so you can style it in a vase, tall bottle or jug that matches your room. Pick up a planter or vase from the rest of the catalogue if you need one.",
      },
      {
        question: "How do I clean artificial flower bunches?",
        answer:
          "Wipe the petals gently with a soft dry cloth. No water, no spray and no polish — dusting is all the upkeep required.",
      },
    ],
  },
];

/** Lookup by URL segment. Returns undefined for an unknown slug so the route can 404 rather than guess. */
export function getCollection(slug: string): CollectionDef | undefined {
  return collections.find((c) => c.slug === slug);
}

/** Every collection path, for the prerender list and the sitemap. Prefixed, not bare slugs. */
export const collectionPaths = collections.map((c) => `/collections/${c.slug}`);
